import { forwardRef, memo, useEffect, useMemo, useRef } from "react";
import { FiClock, FiRefreshCw } from "react-icons/fi";
import { groupActivityByDay } from "../../utils/userActivity";
import { categoryStyle, activityTitle } from "./activityStyle";
import SubjectChip from "./SubjectChip";

const TIME_FORMAT = new Intl.DateTimeFormat("tr-TR", { hour: "2-digit", minute: "2-digit" });

/** Molekül: tek işlem satırı — basınca çekmece. memo: yeni sayfa eklenince eskiler çizilmez. */
const RecentActivityRow = memo(function RecentActivityRow({ item, onSelect, isNew }) {
  const style = categoryStyle(item.category);
  const Icon = style.icon;
  return (
    <li>
      <button
        type="button"
        onClick={() => onSelect(item)}
        className={`flex w-full items-start gap-3 px-4 py-2.5 text-left transition-colors duration-700 hover:bg-gray-50 ${
          isNew ? "bg-indigo-50" : ""
        }`}
      >
        <span className={`mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ring-1 ${style.tone}`}>
          <Icon className="h-4 w-4" />
        </span>
        <span className="flex min-w-0 flex-1 flex-col gap-1">
          <span className="flex items-start justify-between gap-2">
            <span className="min-w-0 text-sm leading-snug text-gray-800">{activityTitle(item)}</span>
            <time className="shrink-0 text-[11px] tabular-nums text-gray-400" dateTime={item.timestamp}>
              {TIME_FORMAT.format(new Date(item.timestamp))}
            </time>
          </span>
          {(item.subject?.label || item.success === false) && (
            <span className="flex min-w-0 flex-wrap items-center gap-1.5 text-[11px]">
              <SubjectChip subject={item.subject} className="max-w-full" />
              {item.success === false && (
                <span className="rounded-md bg-red-50 px-1.5 py-0.5 font-Medium text-red-600">Başarısız</span>
              )}
            </span>
          )}
        </span>
      </button>
    </li>
  );
});

/**
 * Organizma: Son İşlemler açılır paneli (#412, #308). Yalnız gösterir — liste
 * durumu `useActivityFeed`'de (düğmede) yaşar, panel kapanınca kaybolmaz.
 * İskelet yalnız ilk yüklemede; sonraki açılışlarda başlıkta küçük gösterge,
 * yeni kayıtlar üstte vurgulu.
 */
const RecentActivityPanel = forwardRef(function RecentActivityPanel(
  {
    items,
    hasMore,
    isInitialLoading,
    isRefreshing,
    isLoadingMore,
    error,
    highlightIds,
    onLoadMore,
    onRetry,
    onSelect,
    style,
    onMouseEnter,
    onMouseLeave,
  },
  ref
) {
  const sentinelRef = useRef(null);
  const scrollRef = useRef(null);

  // Panelin kendi kaydırma alanında sonsuz kaydırma
  useEffect(() => {
    const node = sentinelRef.current;
    if (!node || !hasMore || error || typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) onLoadMore();
      },
      { root: scrollRef.current, rootMargin: "120px" }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasMore, error, onLoadMore, items.length]);

  const groups = useMemo(() => groupActivityByDay(items), [items]);

  return (
    <div
      ref={ref}
      role="dialog"
      aria-label="Son İşlemler"
      style={style}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className="fixed z-[10200] flex max-h-[70vh] flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl"
    >
      <div className="flex shrink-0 items-center justify-between gap-3 border-b border-gray-100 px-4 py-3">
        <span className="text-sm font-SemiBold text-gray-900">Son İşlemler</span>
        {isRefreshing ? (
          <span className="inline-flex items-center gap-1.5 text-[11px] text-gray-400" role="status">
            <FiRefreshCw className="h-3 w-3 motion-safe:animate-spin" /> Güncelleniyor
          </span>
        ) : (
          <span className="text-[11px] text-gray-400">Son 90 gün</span>
        )}
      </div>

      <div ref={scrollRef} className="min-h-0 flex-1 overflow-y-auto">
        {items.length === 0 && (isInitialLoading || (hasMore && !error)) ? (
          <ul className="space-y-1 px-4 py-3" aria-label="Yükleniyor">
            {Array.from({ length: 5 }).map((_, i) => (
              <li key={i} className="flex gap-3 py-1.5">
                <span className="h-8 w-8 shrink-0 rounded-lg bg-gray-100 motion-safe:animate-pulse" />
                <span className="h-8 flex-1 rounded-lg bg-gray-100 motion-safe:animate-pulse" />
              </li>
            ))}
          </ul>
        ) : items.length === 0 && error ? (
          <div className="flex flex-col items-center gap-3 px-4 py-8 text-center">
            <span className="text-sm text-fifth">{error}</span>
            <button
              type="button"
              onClick={onRetry}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50"
            >
              <FiRefreshCw className="h-3.5 w-3.5" /> Tekrar dene
            </button>
          </div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-2 px-4 py-10 text-center text-gray-400">
            <FiClock className="h-6 w-6" />
            <span className="text-sm font-Medium">Henüz işlem yok</span>
          </div>
        ) : (
          <>
            {groups.map((group) => (
              <section key={group.key}>
                <h3 className="sticky top-0 z-10 bg-white/95 px-4 py-1.5 text-[11px] font-SemiBold text-gray-500 backdrop-blur">
                  {group.label}
                </h3>
                <ul className="divide-y divide-gray-50">
                  {group.items.map((item) => (
                    <RecentActivityRow
                      key={item._id}
                      item={item}
                      onSelect={onSelect}
                      isNew={highlightIds?.has(String(item._id))}
                    />
                  ))}
                </ul>
              </section>
            ))}
            <div ref={sentinelRef} className="h-1" aria-hidden="true" />
            {isLoadingMore && (
              <p className="py-2 text-center text-[11px] text-gray-400 motion-safe:animate-pulse">Daha eski işlemler yükleniyor...</p>
            )}
            {error && (
              <div className="py-2 text-center">
                <button type="button" onClick={onRetry} className="text-xs font-Medium text-indigo-600 hover:underline">
                  Yüklenemedi — tekrar dene
                </button>
              </div>
            )}
            {!hasMore && (
              <p className="py-3 text-center text-[11px] text-gray-400">Son 90 günün tüm işlemleri gösterildi.</p>
            )}
          </>
        )}
      </div>
    </div>
  );
});

export default RecentActivityPanel;
